import { store } from '../state/store';
import { renderSearchPanel } from './search/panel';
import { renderViewerPanel } from './viewer/panel';
import { loadNorma } from './viewer/load';
import type { CordobaOri, Source } from '../api/types';

export function mount(root: HTMLElement): void {
  root.innerHTML = `
    <aside class="search-panel" data-search></aside>
    <div class="search-backdrop" data-backdrop></div>
    <button class="search-wedge" data-search-wedge aria-label="Abrir búsqueda normativa" title="Abrir búsqueda normativa"><i class="ph-bold ph-magnifying-glass"></i></button>
    <main class="viewer-panel" data-viewer></main>
  `;

  const searchEl = root.querySelector<HTMLElement>('[data-search]')!;
  const viewerEl = root.querySelector<HTMLElement>('[data-viewer]')!;
  const backdrop = root.querySelector<HTMLElement>('[data-backdrop]')!;
  const wedgeBtn = root.querySelector<HTMLButtonElement>('[data-search-wedge]')!;
  const mql = window.matchMedia('(max-width: 900px)');
  const WEDGE_HINT_KEY = 'search.wedge.hint.seen.v1';
  let wedgeHintTimer: ReturnType<typeof setTimeout> | null = null;

  const setWedgeState = (narrow: boolean, open: boolean): void => {
    wedgeBtn.classList.toggle('visible', narrow);
    wedgeBtn.classList.toggle('open', narrow && open);
    if (!narrow) return;
    wedgeBtn.innerHTML = open ? '◂' : '<i class="ph-bold ph-magnifying-glass"></i>';
    wedgeBtn.title = open ? 'Contraer búsqueda normativa' : 'Expandir búsqueda normativa';
    wedgeBtn.setAttribute('aria-label', open ? 'Contraer búsqueda normativa' : 'Expandir búsqueda normativa');
  };

  const maybePulseWedge = (): void => {
    if (!wedgeBtn.classList.contains('visible')) return;
    try {
      if (sessionStorage.getItem(WEDGE_HINT_KEY) === '1') return;
      sessionStorage.setItem(WEDGE_HINT_KEY, '1');
    } catch {
      // If sessionStorage is blocked, still show hint once during this page lifetime.
    }
    wedgeBtn.classList.remove('hint-pulse');
    // Force reflow so animation can restart when toggling visibility.
    void wedgeBtn.offsetWidth;
    wedgeBtn.classList.add('hint-pulse');
    if (wedgeHintTimer) clearTimeout(wedgeHintTimer);
    wedgeHintTimer = setTimeout(() => {
      wedgeBtn.classList.remove('hint-pulse');
      wedgeHintTimer = null;
    }, 1500);
  };

  try {
    renderSearchPanel(searchEl);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.error('search mount failed', e);
    searchEl.innerHTML = `<div class="document-error">Error al inicializar la búsqueda: ${msg}</div>`;
  }
  try {
    renderViewerPanel(viewerEl);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.error('viewer mount failed', e);
    viewerEl.innerHTML = `<div class="document-error">Error al inicializar el visor: ${msg}</div>`;
  }

  store.drawerOpen.subscribe((open) => {
    const narrow = mql.matches;
    searchEl.classList.toggle('open', narrow ? open : true);
    backdrop.classList.toggle('open', narrow && open);
    setWedgeState(narrow, open);
    if (narrow && !open) maybePulseWedge();
  });
  
  const closeDrawer = (e?: Event) => {
    if (e && e.type === 'touchend') {
      e.preventDefault(); // Previene que se dispare un evento click "fantasma" después del toque en Android
    }
    store.drawerOpen.set(false);
  };
  backdrop.addEventListener('click', closeDrawer);
  backdrop.addEventListener('touchend', closeDrawer, { passive: false });
  
  wedgeBtn.addEventListener('click', () => {
    const next = !store.drawerOpen.get();
    store.drawerOpen.set(next);
    if (next) (window as unknown as { focusSearch?: () => void }).focusSearch?.();
  });

  const syncDrawerByViewport = (): void => {
    if (mql.matches) {
      // En angosto, iniciar siempre abierto.
      store.drawerOpen.set(true);
    } else {
      store.drawerOpen.set(false);
    }
  };
  mql.addEventListener('change', syncDrawerByViewport);
  syncDrawerByViewport();

  bindShortcuts();
  routeFromHash();
  window.addEventListener('hashchange', routeFromHash);
}

function bindShortcuts(): void {
  document.addEventListener('keydown', (e) => {
    const isMod = e.ctrlKey || e.metaKey;
    const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
    const inField = tag === 'input' || tag === 'textarea' || tag === 'select';

    if (isMod && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      store.drawerOpen.set(true);
      (window as unknown as { focusSearch?: () => void }).focusSearch?.();
      return;
    }
    if (e.key === 'Escape') {
      store.drawerOpen.set(false);
      return;
    }
    if (inField) return;
    if (isMod && e.key.toLowerCase() === 'p') {
      e.preventDefault();
      document.querySelector<HTMLButtonElement>('[data-action="pdf"]')?.click();
    } else if (isMod && e.key.toLowerCase() === 'd') {
      e.preventDefault();
      document.querySelector<HTMLButtonElement>('[data-action="docx"]')?.click();
    } else if (isMod && e.shiftKey && e.key.toLowerCase() === 'r') {
      e.preventDefault();
      // Recargar la norma actual forzando el vaciado de caché (fresh = true)
      const cur = store.currentNorma.get();
      if (cur) {
        void loadNorma(cur.source, cur.id, cur.ori, true);
      }
    }
  });
}

function routeFromHash(): void {
  const m = location.hash.match(/^#\/(n|c|b)\/(?:(L|D)\/)?([^/]+)$/);
  if (!m) return;
  const source: Source = m[1] === 'n' ? 'nacion' : m[1] === 'c' ? 'cordoba' : 'bcra';
  const ori = (m[2] as CordobaOri | undefined) ?? (source === 'cordoba' ? 'L' : undefined);
  const id = decodeURIComponent(m[3]);
  const cur = store.currentNorma.get();
  if (cur && cur.source === source && cur.id === id) return;
  void loadNorma(source, id, ori);
}
